/**
 * AMRGrid — AMR quadtree for the V3 Poisson solver (§4 of Features/Set5.md). Storage per
 * §14 (flat cell array + explicit parent/child indices); cell typing per §3.3/§6.5. */

/** Solver cell type. Numeric so it packs tightly into a typed array if needed.
 *  `SoftPin` (2026-07-30) = Free cell + penalty `k·(H−H_target)`; stays in the Laplace
 *  stencil (k→∞ = Dirichlet, k→0 = Free). Used by `pinType:"soft"`; `rim_soft` is `rim`+`soft`. */
export const enum CellType {
	Free = 0,
	Dirichlet = 1,
	Neumann = 2,
	Barrier = 3,
	SoftPin = 4,
}

/** Face indices: East=0, North=1, West=2, South=3.
 *  These map to grid offsets (+x, +y, -x, -y) in a y-up world. */
export const enum Face {
	E = 0,
	N = 1,
	W = 2,
	S = 3,
}

/** Bitmask helpers for the Neumann face mask. */
export const NEU_E = 1 << Face.E;
export const NEU_N = 1 << Face.N;
export const NEU_W = 1 << Face.W;
export const NEU_S = 1 << Face.S;

/** Convert a Face to the opposite face (for neighbour lookups). */
export function oppositeFace(f: Face): Face {
	// E↔W, N↔S
	return (f ^ 2) as Face;
}

/** Unit axis-aligned step in (x, y) for each face, in cell-index space. */
export const FACE_DX = [1, 0, -1, 0];
export const FACE_DY = [0, 1, 0, -1];

// ============================================================
// Flat-array quadtree
// ============================================================

/**
 * A node in the flat-array AMR quadtree. Interior nodes carry `firstChild` (first of 4
 * contiguous children); leaves carry `cell` (index into leaf-cell arrays). Root = node 0. */
export interface QuadNode {
	/** Cell AABB in world meters. */
	minX: number; minY: number; maxX: number; maxY: number;
	/** Refinement level: 0 = world root. */
	level: number;
	/** Parent node index, or -1 for the root. */
	parent: number;
	/** Quad-order child index within parent (0..3), or -1 for root. Order is [BL, BR, TL, TR]. */
	quadIndex: number;
	/** For interior nodes: index of first child (children are firstChild+0..+3). -1 for leaves. */
	firstChild: number;
	/** For leaves: index into the leaf-cell solver-state arrays (the `cells` flat arrays). -1 for interior nodes. */
	cellIndex: number;
}

/** Leaf-cell solver state — stored as parallel typed arrays indexed by `cellIndex` for
 *  cache-friendly V-cycle sweeps. Mirrors the `SolverCell` interface in the plan but in
 *  SoA (struct-of-arrays) form for the hot path. */
export interface CellArrays {
	/** One of CellType values per cell. */
	type: Uint8Array;
	/** Current solution estimate H_{i,j}. */
	H: Float64Array;
	/** Dirichlet pinned value, or Neumann g₀·Δ·sign on the face. */
	bcValue: Float64Array;
	/** Neumann face bitmask (4 bits). 0 = no Neumann faces. */
	neumannFaces: Uint8Array;
	/** Diffusion coefficient at cell center. Default 1. */
	c: Float64Array;
	/** Source term at cell center. Default 0 (= Laplace). */
	f: Float64Array;
	/** Neumann g₀ per face: 4 values per cell. layout: [cell*4 + face]. */
	neumannG0: Float64Array;
	/** **2026-07-30 — soft-pin target** (only meaningful when `type === CellType.SoftPin`).
	 *  The elevation the cell is pulled toward by the penalty term `k·(H − target)`. */
	softPinTarget: Float64Array;
	/** **2026-07-30 — soft-pin stiffness** `k` (only meaningful when `type === CellType.SoftPin`).
	 *  Larger k → stronger pull toward `softPinTarget` (k→∞ = Dirichlet; k→0 = pure Free). */
	softPinStiffness: Float64Array;
	/** **2026-08-02 — per-cell pin dominance.** Meaningful for the value-pin types
	 *  (Dirichlet/SoftPin); 0 otherwise. Set by `writeDirichlet`/`writeSoftPin`. A later
	 *  value-pin write is blocked iff `dominance[cellIdx] > newDominance`; default 0. */
	dominance: Float64Array;
	/** True if cell is within the current camera-view refinement region. */
	inViewRegion: Uint8Array;
	/** Number of cells actually used (the arrays may be over-allocated). */
	count: number;
}

/** The whole AMR grid: a flat node array + parallel leaf-cell arrays. */
export interface AMRGrid {
	/** World bounding box that the root cell covers. */
	worldMinX: number; worldMinY: number;
	worldMaxX: number; worldMaxY: number;
	/** Root cell side length (max of width/height; we make the root square for clean halvings). */
	rootSize: number;
	/** Maximum refinement depth. Cell size at level L = rootSize / 2^L. */
	maxDepth: number;

	/** Flat array of quadtree nodes. Node 0 is the root. Grows as we refine. */
	nodes: QuadNode[];
	/** Parallel leaf-cell solver-state arrays. */
	cells: CellArrays;

	/** **Perf Tier-1 #1 — neighbour index cache.** `neighbours[nodeIdx*4 + face]` = the
	 *  cellIndex of the leaf neighbour across `face`, or -1 (out-of-world / not-a-leaf).
	 *  Built by {@link ensureNeighbourCache}; `undefined` ⇒ not yet built. */
	neighbours?: Int32Array;

	/** **Perf pre-GPU — cell-indexed neighbour cache.** `neighboursByCell[cellIdx*4+face]`
	 *  = neighbour cellIndex or -1; parallel to `neighbours` but keyed by cellIdx for GPU
	 *  compute shaders (packed cell arrays, not tree node indices). */
	neighboursByCell?: Int32Array;

	/** **Perf pre-GPU — red leaf node-index list.** Leaf nodeIdx values with (ix+iy) even.
	 *  Pre-computed by {@link buildNeighbourCache} so `gaussSeidelSweep` iterates directly.
	 *  Uniform grids only; `undefined` on AMR grids / before the cache is built. */
	redLeaves?: Int32Array;

	/** **Perf pre-GPU — black leaf node-index list.** Complement of {@link redLeaves}:
	 *  leaf `nodeIdx` values with (ix+iy) odd. */
	blackLeaves?: Int32Array;

	/** **Perf Set3+ — uniform-grid leaf lattice map (evalH O(1) gather).** Built by
	 *  {@link buildNeighbourCache} for uniform grids: O(1) `map[iy*n+ix] → cellIndex`
	 *  vs 17 descents/sample (2026-07-25 Step 1). Nulled by `refineNode`. */
	leafLattice?: LeafLatticeMap;

	/** **Galerkin coarse operator (Tier 3 #8 Approach 2).** CSR stencil of `A_c = R·A_f·P`
	 *  (bilinear P, R = ½·Pᵀ). Coarse branches read it instead of re-deriving the 5-point
	 *  stencil. `rhsNeuSum` = pre-summed Neumann RHS (don't re-derive g0 — double-counts). */
	galerkinStencil?: GalerkinStencil;
}

/** Uniform-grid leaf lattice map for O(1) `evalH` gathered bicubic samples. See
 *  {@link AMRGrid.leafLattice}. Built for uniform grids only — `undefined` on AMR grids.
 *  Mirrors `WebGPU/GPUResources.ts:buildFlatToNativeMap`'s GPU packed-cell geometry. */
export interface LeafLatticeMap {
	/** `map[iy * rowStride + ix]` = cellIndex of the leaf at lattice slot (ix, iy); `-1`
	 *  unreachable on a true uniform grid — {@link evalH} defensively reads `0` for any
	 *  `-1` (matches `packCells`' Dirichlet-0 defensive default). */
	map: Int32Array;
	/** Lattice width (`= 2^level`). */
	rowStride: number;
	/** Lattice height (equal to `rowStride` for a square root). */
	rowCount: number;
	/** Cell side length in world units (`= grid.rootSize / rowStride`). */
	cellSize: number;
	/** World-min of the root AABB (the lattice origin — both coords of the root's `minX/minY`). */
	rootMinX: number;
	rootMinY: number;
	/** The single level every leaf sits at (the uniform-grid invariant). */
	level: number;
}

/** Precomputed Galerkin coarse operator `A_c = R·A_f·P` in CSR form, one row per coarse
 *  cellIndex. See {@link AMRGrid.galerkinStencil}. */
export interface GalerkinStencil {
	/** Length = `cells.count`. Non-negative diagonal of `A_c` for free/Dirichlet rows;
	 *  `1` for Dirichlet rows (identity), `0` for Barrier rows (excluded). */
	diag: Float64Array;
	/** CSR column indices (cellIndex of the off-diagonal neighbour). Length = `rowEnd[count]`. */
	cols: Int32Array;
	/** CSR values (the off-diagonal coefficient — typically `-c·w/Δ²`). Length = `rowEnd[count]`. */
	vals: Float64Array;
	/** `rowEnd[cellIdx]` = end offset into `cols`/`vals` for `cellIdx`'s row (cumulative;
	 *  `rowEnd[-1]` is read as 0, so `rowStart = cellIdx>0 ? rowEnd[cellIdx-1] : 0`). */
	rowEnd: Int32Array;
	/** Length = `cells.count`. Dirichlet rows: pinned `bcValue` so the Galerkin row returns
	 *  `b = rhsBase`, `A_ii = 1`. Free rows: `0` (RHS = restricted residual in `cells.f`;
	 *  don't re-derive `neumannG0` on Galerkin levels — double-counts). Barrier rows: `0`. */
	rhsBase: Float64Array;
}

/** Quad-order constants: child i of a parent occupies quadrant [BL, BR, TL, TR]. */
export const QUAD_BL = 0, QUAD_BR = 1, QUAD_TL = 2, QUAD_TR = 3;

/** Given a parent AABB and a quad index (0..3), return the child's AABB. */
export function childAABB(
	pMinX: number, pMinY: number, pMaxX: number, pMaxY: number,
	quadIndex: number,
): {minX: number; minY: number; maxX: number; maxY: number} {
	const midX = (pMinX + pMaxX) * 0.5;
	const midY = (pMinY + pMaxY) * 0.5;
	switch (quadIndex) {
		case QUAD_BL: return {minX: pMinX, minY: pMinY, maxX: midX,   maxY: midY};
		case QUAD_BR: return {minX: midX,  minY: pMinY, maxX: pMaxX, maxY: midY};
		case QUAD_TL: return {minX: pMinX, minY: midY,  maxX: midX,  maxY: pMaxY};
		case QUAD_TR: return {minX: midX,  minY: midY,  maxX: pMaxX, maxY: pMaxY};
		default: throw new Error(`Invalid quadIndex ${quadIndex}`);
	}
}

/** Find the quad index of a point relative to a parent cell's center. */
export function quadIndexForPoint(parentCenterX: number, parentCenterY: number, px: number, py: number): number {
	const right = px >= parentCenterX;
	const top = py >= parentCenterY;
	return (top ? 2 : 0) + (right ? 1 : 0);
}

/** Cell side length at a given level. 2026-07-25 Step 2a: `Math.pow(2, level)` →
 *  `1 << level` (integer shift, exact for level ≤ 31; pow was 22.85 ms self-time in
 *  Trace-20260725T165339.json; shift is one CPU instruction, byte-identical output). */
export function cellSizeAtLevel(grid: AMRGrid, level: number): number {
	return grid.rootSize / (1 << level);
}

/** Level needed to reach a target cell size (ceil-log2 of rootSize/targetSize). */
export function levelForCellSize(rootSize: number, targetSize: number, maxDepth: number): number {
	if (targetSize <= 0) return maxDepth;
	const ratio = rootSize / targetSize;
	if (ratio <= 1) return 0;
	const lvl = Math.ceil(Math.log2(ratio));
	return Math.max(0, Math.min(maxDepth, lvl));
}

// ============================================================
// Grid construction
// ============================================================

/** Build a fresh AMR grid covering the given world AABB, with only the root node present
 *  (no leaves yet, no refinement). Caller refines via `refineNode` and rasterizes
 *  constraint cells onto the resulting leaves. */
export function createAMRGrid(
	worldMinX: number, worldMinY: number,
	worldMaxX: number, worldMaxY: number,
	maxDepth: number,
	initialCellCapacity = 256,
): AMRGrid {
	// Make the root square: side = max(width, height). The world bbox may be non-square; we
	// accept a square root that covers it (extra padding is fine — outside-feature cells stay
	// Free and adopt the outer BC).
	const w = worldMaxX - worldMinX;
	const h = worldMaxY - worldMinY;
	const rootSize = Math.max(w, h);
	// Re-centre the root on the world bbox centre so padding is symmetric.
	const cx = (worldMinX + worldMaxX) * 0.5;
	const cy = (worldMinY + worldMaxY) * 0.5;
	const rootMinX = cx - rootSize * 0.5;
	const rootMinY = cy - rootSize * 0.5;
	const rootMaxX = cx + rootSize * 0.5;
	const rootMaxY = cy + rootSize * 0.5;

	const root: QuadNode = {
		minX: rootMinX, minY: rootMinY, maxX: rootMaxX, maxY: rootMaxY,
		level: 0, parent: -1, quadIndex: -1,
		firstChild: -1, cellIndex: -1,
	};

	const cells: CellArrays = {
		type: new Uint8Array(initialCellCapacity),
		H: new Float64Array(initialCellCapacity),
		bcValue: new Float64Array(initialCellCapacity),
		neumannFaces: new Uint8Array(initialCellCapacity),
		c: new Float64Array(initialCellCapacity),
		f: new Float64Array(initialCellCapacity),
		neumannG0: new Float64Array(initialCellCapacity * 4),
		softPinTarget: new Float64Array(initialCellCapacity),
		softPinStiffness: new Float64Array(initialCellCapacity),
		dominance: new Float64Array(initialCellCapacity),
		inViewRegion: new Uint8Array(initialCellCapacity),
		count: 0,
	};

	return {
		worldMinX, worldMinY, worldMaxX, worldMaxY,
		rootSize, maxDepth,
		nodes: [root],
		cells,
	};
}

/** Ensure the cell arrays have at least `needed` slots. Grows geometrically.
 *  Preserves all existing per-cell data including the per-face `neumannG0` layout. */
export function ensureCellCapacity(grid: AMRGrid, needed: number): void {
	const cells = grid.cells;
	if (cells.type.length >= needed) return;
	let newCap = cells.type.length;
	while (newCap < needed) newCap *= 2;
	const growF64 = (a: Float64Array) => {
		const n = new Float64Array(newCap); n.set(a); return n;
	};
	const growU8 = (a: Uint8Array) => {
		const n = new Uint8Array(newCap); n.set(a); return n;
	};
	cells.type = growU8(cells.type);
	cells.H = growF64(cells.H);
	cells.bcValue = growF64(cells.bcValue);
	cells.neumannFaces = growU8(cells.neumannFaces);
	cells.c = growF64(cells.c);
	cells.f = growF64(cells.f);
	// neumannG0 is laid out as [cell*4 + face]; preserve by copying into the wider array.
	const newG0 = new Float64Array(newCap * 4);
	for (let i = 0; i < cells.count; i++) {
		for (let f = 0; f < 4; f++) newG0[i * 4 + f] = cells.neumannG0[i * 4 + f];
	}
	cells.neumannG0 = newG0;
	cells.softPinTarget = growF64(cells.softPinTarget);
	cells.softPinStiffness = growF64(cells.softPinStiffness);
	cells.dominance = growF64(cells.dominance);
	cells.inViewRegion = growU8(cells.inViewRegion);
}

/** Append a new leaf cell to the cell arrays and return its cellIndex. */
function allocCell(grid: AMRGrid): number {
	const cells = grid.cells;
	if (cells.count >= cells.type.length) ensureCellCapacity(grid, cells.count + 1);
	const idx = cells.count;
	// Initialize to Free / zero — caller overwrites as needed.
	cells.type[idx] = CellType.Free;
	cells.H[idx] = 0;
	cells.bcValue[idx] = 0;
	cells.neumannFaces[idx] = 0;
	cells.c[idx] = 1;
	cells.f[idx] = 0;
	for (let f = 0; f < 4; f++) cells.neumannG0[idx * 4 + f] = 0;
	cells.softPinTarget[idx] = 0;
	cells.softPinStiffness[idx] = 0;
	cells.dominance[idx] = 0;
	cells.inViewRegion[idx] = 0;
	cells.count++;
	return idx;
}

/** Split a leaf node into 4 children. The node becomes interior (loses its cell slot —
 *  the cell value is propagated to the 4 children as their initial H). Returns the range
 *  [firstChild, firstChild+3]. Idempotent if the node is already interior. */
export function refineNode(grid: AMRGrid, nodeIdx: number): number {
	const node = grid.nodes[nodeIdx];
	if (node.firstChild >= 0) return node.firstChild;

	// Tier 1 perf #1: the neighbour cache (if any) is now stale (leaf → interior).
	// Invalidate; `ensureNeighbourCache` rebuilds before the next solve. Refinement
	// only happens at rasterize time (never during a solve), so this is cheap.
	if (grid.neighbours !== undefined) grid.neighbours = undefined;
	grid.neighboursByCell = undefined;
	grid.redLeaves = undefined;
	grid.blackLeaves = undefined;
	grid.leafLattice = undefined;

	const firstChild = grid.nodes.length;
	// Pre-grow the nodes array by 4.
	for (let q = 0; q < 4; q++) {
		const ab = childAABB(node.minX, node.minY, node.maxX, node.maxY, q);
		const childCellIdx = allocCell(grid);
		// Inherit parent's H as initial guess (keeps multigrid cycles stable across refinement).
		const parentH = node.cellIndex >= 0 ? grid.cells.H[node.cellIndex] : 0;
		grid.cells.H[childCellIdx] = parentH;
		// Inherit parent's c/f — most common case (region overrides set per-cell later).
		if (node.cellIndex >= 0) {
			grid.cells.c[childCellIdx] = grid.cells.c[node.cellIndex];
			grid.cells.f[childCellIdx] = grid.cells.f[node.cellIndex];
		}
		grid.nodes.push({
			minX: ab.minX, minY: ab.minY, maxX: ab.maxX, maxY: ab.maxY,
			level: node.level + 1, parent: nodeIdx, quadIndex: q,
			firstChild: -1, cellIndex: childCellIdx,
		});
	}

	// Mark parent interior; its old cell slot is released (we just decrement count if it was
	// the last-allocated cell, otherwise it's left as a dead slot — acceptable, leaves list
	// is rebuilt by `collectLeaves` and skips interior nodes).
	node.firstChild = firstChild;
	node.cellIndex = -1;
	return firstChild;
}

// ============================================================
// Traversal helpers
// ============================================================

/** Find the leaf node whose cell contains the world point (x, y). Returns the node index. */
export function findLeafContaining(grid: AMRGrid, x: number, y: number): number {
	let nodeIdx = 0;
	while (true) {
		const node = grid.nodes[nodeIdx];
		if (node.firstChild < 0) return nodeIdx;
		const cx = (node.minX + node.maxX) * 0.5;
		const cy = (node.minY + node.maxY) * 0.5;
		const q = quadIndexForPoint(cx, cy, x, y);
		nodeIdx = node.firstChild + q;
	}
}

/** **2026-07-25 eval-block perf Step 3 — ancestor-relative descent.** Descend from a given
 *  starting node index (must be an ancestor/equal of the target leaf). Used by
 *  `evalHLegacyDescent`'s 4×4 gather: walk up 2–3 hops to the nearest ancestor, then descend. */
export function findLeafFromAncestor(grid: AMRGrid, x: number, y: number, startNodeIdx: number): number {
	let nodeIdx = startNodeIdx;
	while (true) {
		const node = grid.nodes[nodeIdx];
		if (node.firstChild < 0) return nodeIdx;
		const cx = (node.minX + node.maxX) * 0.5;
		const cy = (node.minY + node.maxY) * 0.5;
		const q = quadIndexForPoint(cx, cy, x, y);
		nodeIdx = node.firstChild + q;
	}
}

/** **2026-07-25 eval-block perf Step 3 — last-leaf memoising descent.** A caller-supplied
 *  1-slot "last leaf" ref: if `(x, y)` still lies inside the last-found leaf's AABB, return
 *  it — zero descent. Row-major chunk samplers hit the same leaf ~33 consecutive times. */
export function findLeafContainingMemo(grid: AMRGrid, x: number, y: number, memo: {idx: number}): number {
	const last = memo.idx;
	if (last >= 0) {
		const n = grid.nodes[last];
		if (x >= n.minX && x < n.maxX && y >= n.minY && y < n.maxY) return last;
	}
	const result = findLeafContaining(grid, x, y);
	memo.idx = result;
	return result;
}

/** Collect all leaf node indices in BFS order. Used to rebuild the flat leaves list after
 *  refinement changes. */
export function collectLeaves(grid: AMRGrid): number[] {
	const out: number[] = [];
	const stack: number[] = [0];
	while (stack.length > 0) {
		const nodeIdx = stack.pop()!;
		const node = grid.nodes[nodeIdx];
		if (node.firstChild < 0) {
			out.push(nodeIdx);
		} else {
			// Push in reverse so BL is processed first (maintains spatial BFS-ish order).
			for (let q = 3; q >= 0; q--) stack.push(node.firstChild + q);
		}
	}
	return out;
}

/** Collect leaves grouped by level: out[level] = array of node indices at that level. */
export function collectLeavesByLevel(grid: AMRGrid, maxLevel: number): number[][] {
	const out: number[][] = Array.from({length: maxLevel + 1}, ()=>[]);
	for (const nodeIdx of collectLeaves(grid)) {
		const node = grid.nodes[nodeIdx];
		if (node.level <= maxLevel) out[node.level].push(nodeIdx);
	}
	return out;
}

/** Iterate every leaf contained within a world-space rectangle (inclusive). */
export function leavesInRect(
	grid: AMRGrid,
	minX: number, minY: number, maxX: number, maxY: number,
	visit: (nodeIdx: number)=>void,
): void {
	const stack: number[] = [0];
	while (stack.length > 0) {
		const nodeIdx = stack.pop()!;
		const n = grid.nodes[nodeIdx];
		// AABB rejection
		if (n.maxX < minX || n.minX > maxX || n.maxY < minY || n.minY > maxY) continue;
		if (n.firstChild < 0) {
			visit(nodeIdx);
		} else {
			for (let q = 3; q >= 0; q--) stack.push(n.firstChild + q);
		}
	}
}

/** 2026-08-03 rapid-zoom-queue fix — collect every leaf within a world-space rectangle into
 *  an array (the array variant of {@link leavesInRect}), for rasterize hot loops that
 *  batch-iterate with `await signal.maybeYield()` (the callback API can't await). */
export function collectLeavesInRect(
	grid: AMRGrid,
	minX: number, minY: number, maxX: number, maxY: number,
): number[] {
	const out: number[] = [];
	leavesInRect(grid, minX, minY, maxX, maxY, (nodeIdx)=>{ out.push(nodeIdx); });
	return out;
}

/** Iterate every leaf whose center lies within a world-space rectangle (inclusive). */
export function leavesWithCenterInRect(
	grid: AMRGrid,
	minX: number, minY: number, maxX: number, maxY: number,
	visit: (nodeIdx: number)=>void,
): void {
	leavesInRect(grid, minX, minY, maxX, maxY, (nodeIdx)=>{
		const n = grid.nodes[nodeIdx];
		const cx = (n.minX + n.maxX) * 0.5;
		const cy = (n.minY + n.maxY) * 0.5;
		if (cx >= minX && cx <= maxX && cy >= minY && cy <= maxY) visit(nodeIdx);
	});
}

/** Iterate every leaf whose cell center is within `radius` of (px, py). */
export function leavesWithinRadius(
	grid: AMRGrid,
	px: number, py: number, radius: number,
	visit: (nodeIdx: number)=>void,
): void {
	leavesInRect(grid, px - radius, py - radius, px + radius, py + radius, (nodeIdx)=>{
		const n = grid.nodes[nodeIdx];
		const cx = (n.minX + n.maxX) * 0.5;
		const cy = (n.minY + n.maxY) * 0.5;
		const dx = cx - px, dy = cy - py;
		if (dx * dx + dy * dy <= radius * radius) visit(nodeIdx);
	});
}

// ============================================================
// Neighbour lookup (across AMR levels)
// ============================================================

/** Result of a neighbour lookup across the quadtree. */
export interface NeighbourResult {
	/** Node index of the neighbour, or -1 if outside the world (no neighbour — handle via BC). */
	nodeIdx: number;
	/** True if the neighbour is at a coarser level (across an AMR refinement boundary). */
	coarser: boolean;
}

/** Find the leaf neighbour of `nodeIdx` across `face`. Returns {nodeIdx: -1} if outside the
 *  world root. If the neighbour is finer, returns the single finest leaf sharing the face's
 *  full edge whose center projects onto the face (there may be 2); use {@link neighboursAcrossFace} for all. */
export function neighbourAcrossFace(grid: AMRGrid, nodeIdx: number, face: Face): NeighbourResult {
	const node = grid.nodes[nodeIdx];
	// Compute the midpoint of the shared face, offset slightly into the neighbour's cell.
	const cx = (node.minX + node.maxX) * 0.5;
	const cy = (node.minY + node.maxY) * 0.5;
	const cellSize = (node.maxX - node.minX);
	const eps = cellSize * 1e-3;
	let tx = cx, ty = cy;
	switch (face) {
		case Face.E: tx = node.maxX + eps; break;
		case Face.N: ty = node.maxY + eps; break;
		case Face.W: tx = node.minX - eps; break;
		case Face.S: ty = node.minY - eps; break;
	}
	// Outside world root?
	const root = grid.nodes[0];
	if (tx < root.minX || tx > root.maxX || ty < root.minY || ty > root.maxY) {
		return {nodeIdx: -1, coarser: false};
	}
	const neighbourIdx = findLeafContaining(grid, tx, ty);
	const neighbour = grid.nodes[neighbourIdx];
	return {
		nodeIdx: neighbourIdx,
		coarser: neighbour.level < node.level,
	};
}

/** All leaf neighbours across `face` (handles the AMR case where the neighbour level is finer
 *  and there are 2 leaves sharing the face edge). Returns an array of node indices. */
export function neighboursAcrossFace(grid: AMRGrid, nodeIdx: number, face: Face): number[] {
	const node = grid.nodes[nodeIdx];
	const cellSize = (node.maxX - node.minX);
	// Sample 2 points along the face edge: at 25% and 75% of the edge length.
	const pts: Array<{x: number; y: number}> = [];
	const eps = cellSize * 1e-3;
	switch (face) {
		case Face.E:
			pts.push({x: node.maxX + eps, y: node.minY + cellSize * 0.25});
			pts.push({x: node.maxX + eps, y: node.minY + cellSize * 0.75});
			break;
		case Face.N:
			pts.push({x: node.minX + cellSize * 0.25, y: node.maxY + eps});
			pts.push({x: node.minX + cellSize * 0.75, y: node.maxY + eps});
			break;
		case Face.W:
			pts.push({x: node.minX - eps, y: node.minY + cellSize * 0.25});
			pts.push({x: node.minX - eps, y: node.minY + cellSize * 0.75});
			break;
		case Face.S:
			pts.push({x: node.minX + cellSize * 0.25, y: node.minY - eps});
			pts.push({x: node.minX + cellSize * 0.75, y: node.minY - eps});
			break;
	}
	const root = grid.nodes[0];
	const out: number[] = [];
	const seen = new Set<number>();
	for (const p of pts) {
		if (p.x < root.minX || p.x > root.maxX || p.y < root.minY || p.y > root.maxY) continue;
		const idx = findLeafContaining(grid, p.x, p.y);
		if (!seen.has(idx)) { seen.add(idx); out.push(idx); }
	}
	return out;
}

// ============================================================
// Neighbour index cache (Tier 1 perf #1)
// ============================================================

/** Build (or rebuild) the flat neighbour-cache on `grid`. For every leaf `L` and face
 *  `F ∈ {0..3}` stores `neighbours[L*4+F]` = the neighbour's cellIndex, or -1 (out-of-world;
 *  interior nodes keep -1). One-time O(nodeCount × 4 × depth) cost — the dominant V-cycle win. */
export function buildNeighbourCache(grid: AMRGrid): void {
	const nodeCount = grid.nodes.length;
	const cache = new Int32Array(nodeCount * 4).fill(-1);
	grid.neighbours = cache;

	// Cell-indexed parallel cache (GPU-port prerequisite).
	const cellCache = new Int32Array(grid.cells.count * 4).fill(-1);
	grid.neighboursByCell = cellCache;

	// Collect leaf nodes so we can partition them into red / black for the GS sweep.
	const root = grid.nodes[0];
	const rootMinX = root.minX;
	const rootMinY = root.minY;
	const redBuf: number[] = [];
	const blackBuf: number[] = [];
	// Whether the grid is uniform (all leaves at the same level) — only uniform grids get
	// a meaningful red/black split (AMR grids use lexicographic GS or the Galerkin path).
	let uniformLevel = -1;
	let isUniform = true;

	for (let nodeIdx = 0; nodeIdx < nodeCount; nodeIdx++) {
		const node = grid.nodes[nodeIdx];
		if (node.firstChild >= 0) continue; // interior node: leave the 4 slots at -1.
		const base = nodeIdx * 4;
		const cellIdx = node.cellIndex;
		const cellBase = cellIdx * 4;
		// Reuse the existing descent-based neighbour finder once per face.
		for (let face = 0; face < 4; face++) {
			const r = neighbourAcrossFace(grid, nodeIdx, face as Face);
			if (r.nodeIdx < 0) {
				cache[base + face] = -1;
				cellCache[cellBase + face] = -1;
			} else {
				const nCell = grid.nodes[r.nodeIdx].cellIndex;
				// A neighbour node that is somehow not a leaf (cellIndex < 0) is treated as
				// "no neighbour" — the solver already guards against this with its `>= 0` checks.
				cache[base + face] = nCell;
				cellCache[cellBase + face] = nCell;
			}
		}
		// Checkerboard colour: integer grid coordinates at this node's level.
		const Δ = node.maxX - node.minX;
		const ix = Math.round(((node.minX + node.maxX) * 0.5 - rootMinX) / Δ - 0.5);
		const iy = Math.round(((node.minY + node.maxY) * 0.5 - rootMinY) / Δ - 0.5);
		if (((ix + iy) & 1) === 0) redBuf.push(nodeIdx); else blackBuf.push(nodeIdx);
		// Uniformity check for the red/black split to be meaningful.
		if (isUniform) {
			if (uniformLevel < 0) uniformLevel = node.level;
			else if (node.level !== uniformLevel) isUniform = false;
		}
	}
	if (isUniform) {
		grid.redLeaves = new Int32Array(redBuf);
		grid.blackLeaves = new Int32Array(blackBuf);
		// **Perf Set3+ — uniform-grid leaf lattice map (evalH O(1) gather).** Same geometry
		// `WebGPU/GPUResources.ts:buildFlatToNativeMap` derives for the GPU packed cells, kept
		// on the TS side so `evalH` avoids a dynamic import; one extra O(N) pass here.
		const n = 1 << uniformLevel;
		const lm = new Int32Array(n * n).fill(-1);
		const cellSize = grid.rootSize / n;
		for (let nodeIdx = 0; nodeIdx < nodeCount; nodeIdx++) {
			const node = grid.nodes[nodeIdx];
			if (node.firstChild >= 0) continue;
			const Δ = node.maxX - node.minX;
			const ix = Math.round(((node.minX + node.maxX) * 0.5 - rootMinX) / Δ - 0.5);
			const iy = Math.round(((node.minY + node.maxY) * 0.5 - rootMinY) / Δ - 0.5);
			if (ix >= 0 && ix < n && iy >= 0 && iy < n) lm[iy * n + ix] = node.cellIndex;
		}
		grid.leafLattice = {
			map: lm, rowStride: n, rowCount: n, cellSize,
			rootMinX, rootMinY, level: uniformLevel,
		};
	} else {
		// AMR / Galerkin grids use lexicographic GS — red/black is not used, and the lattice
		// map is invalid (a per-feature refined pocket breaks the row-major uniform lattice).
		grid.redLeaves = undefined;
		grid.blackLeaves = undefined;
		grid.leafLattice = undefined;
	}
}

/** Lazily ensure `grid.neighbours` is populated. Call at the top of any solver entry that
 *  reads neighbours in its hot path. Idempotent — re-uses a stale cache if present; the
 *  cache is only stale when the leaf set changed. Also populates {@link AMRGrid.leafLattice}. */
export function ensureNeighbourCache(grid: AMRGrid): void {
	if (grid.neighbours === undefined) buildNeighbourCache(grid);
}

/** Lazily ensure {@link AMRGrid.leafLattice} is populated. Idempotent: re-uses a stale
 *  lattice map if present; falls back to `leafLattice === undefined` — callers ({@link
 *  evalH}) then use the legacy root-descent path (AMR grids, never-cache-built grids). */
export function ensureLeafLattice(grid: AMRGrid): void {
	if (grid.neighbours === undefined) buildNeighbourCache(grid);
}
